import { useEffect, useMemo, useState } from 'react';

import { apiUrl, endpoints } from '../config';
import { LegalDialog } from '../components/LegalDialog';

/**
 * §4.2 Registration and onboarding.
 *
 * Registration belongs to the public website, not to the application: the person filling this in
 * has no account, no tenant and nothing to sign in to yet. Until now "Start free trial" pointed
 * at the sign-in form, which is a door with no handle — the API had a signup endpoint and nothing
 * in front of it.
 *
 * The form asks for the minimum the platform needs to provision a tenant and stops. Legal name,
 * tax identifier, billing address, fiscal year and the rest of §4.2 are collected in the guided
 * setup once there is somewhere to put them; asking for a tax number before somebody has decided
 * to try the product loses the ones who had not decided yet.
 */

interface SignupResult {
  slug: string;
  emailVerificationRequired: boolean;
  trialEndsAtUtc?: string | null;
}

interface CataloguePlan {
  planKey: string;
  name: string;
  applicationKey?: string | null;
  trialDays: number;
}

const countries = [
  { code: 'SG', name: 'Singapore' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
];

/** Mirrors the server's rules so the first attempt is not a rejection. */
const passwordRules = [
  { test: (p: string) => p.length >= 8, label: 'At least 8 characters' },
  { test: (p: string) => /[A-Z]/.test(p), label: 'An uppercase letter' },
  { test: (p: string) => /[a-z]/.test(p), label: 'A lowercase letter' },
  { test: (p: string) => /\d/.test(p), label: 'A digit' },
  { test: (p: string) => /[^A-Za-z0-9]/.test(p), label: 'A symbol' },
];

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export default function SignUp({ navigate }: { navigate: (to: string) => void }) {
  const chosenPlan = useMemo(
    () => new URLSearchParams(window.location.search).get('plan'),
    [],
  );

  const [companyName, setCompanyName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugState, setSlugState] = useState<'idle' | 'checking' | 'free' | 'taken'>('idle');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('SG');
  const [accepted, setAccepted] = useState(false);

  const [busy, setBusy] = useState(false);

  /**
   * Start with example records rather than empty screens.
   *
   * On by default. A trial that opens on nothing is a trial nobody finishes — there is no
   * pipeline to look at, no stock to reconcile, no supplier to open — and somebody who would
   * rather start clean unticks one box.
   */
  const [withSamples, setWithSamples] = useState(true);

  /** Which legal document the dialog is showing, or null when it is closed. */
  const [legal, setLegal] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<SignupResult | null>(null);

  /*
   * Which application the chosen plan actually sells.
   *
   * There is no "pick your applications" step here on purpose. A chooser listing five products
   * where three cannot be selected advertises a disappointment at the moment somebody commits.
   * The specification says the same thing (§4.2): choose a plan, with applications that are not
   * built yet shown only as catalogue items.
   *
   * So the plan decides, rather than a hard-coded 'crm'. That design has now paid for itself
   * once: SRM plans were added to the catalogue and a visitor arriving from an SRM price card
   * signs up for SRM without a line of this file changing. Adding a second application
   * afterwards is a billing action, which is where entitlements already live.
   */
  const [plans, setPlans] = useState<CataloguePlan[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    fetch(apiUrl('/api/v1/public/catalogue'), { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((data: { plans: CataloguePlan[] }) => setPlans(data.plans))
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  const plan = plans.find((p) => p.planKey === chosenPlan) ?? null;
  const applicationKey = plan?.applicationKey ?? 'crm';

  // The identifier follows the company name until somebody edits it themselves, at which point
  // it is theirs and must stop moving underneath them.
  const effectiveSlug = slugTouched ? slug : slugify(companyName);

  useEffect(() => {
    if (!effectiveSlug || effectiveSlug.length < 2) {
      setSlugState('idle');
      return;
    }

    setSlugState('checking');
    const controller = new AbortController();

    // Debounced: a check per keystroke would ask the server a question the user has not
    // finished asking.
    const timer = window.setTimeout(() => {
      fetch(apiUrl(`/api/v1/auth/slug-available?slug=${encodeURIComponent(effectiveSlug)}`), {
        signal: controller.signal,
      })
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then((data: { available: boolean }) => setSlugState(data.available ? 'free' : 'taken'))
        .catch((failure) => {
          if ((failure as { name?: string })?.name !== 'AbortError') setSlugState('idle');
        });
    }, 400);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [effectiveSlug]);

  const unmet = passwordRules.filter((rule) => !rule.test(password));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!accepted) {
      setError('Please accept the terms before continuing.');
      return;
    }

    if (unmet.length > 0) {
      setError('The password does not meet the requirements listed below it.');
      return;
    }

    setBusy(true);

    try {
      const response = await fetch(apiUrl('/api/v1/auth/signup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: companyName.trim(),
          email: email.trim(),
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          countryCode: country,
          slug: effectiveSlug || null,
          planKey: chosenPlan,
          applicationKeys: [applicationKey],
          acceptedTerms: true,
          includeSampleData: withSamples,
        }),
      });

      const body = await response.json().catch(() => null);

      if (!response.ok) {
        // The API answers with a problem document; its detail is written for the person reading
        // it, so it is shown rather than replaced with something vaguer.
        setError(
          (body as { detail?: string; title?: string })?.detail ??
            (body as { title?: string })?.title ??
            'The account could not be created. Please try again.',
        );
        return;
      }

      setDone(body as SignupResult);
      window.scrollTo({ top: 0 });
    } catch {
      setError('We could not reach ClientMa. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="section">
        <div className="wrap" style={{ maxWidth: 640 }}>
          <span className="eyebrow">Almost there</span>
          <h1 style={{ marginBottom: 16 }}>Your organisation is being set up</h1>

          {done.emailVerificationRequired && (
            <p>
              We have sent a message to <strong>{email}</strong>. Open it and confirm the address
              to finish — the link expires, so it is worth doing now.
            </p>
          )}

          <div className="card" style={{ marginTop: 24 }}>
            <h3>What happens next</h3>
            <ul className="ticks" style={{ marginTop: 14 }}>
              <li>Your workspace, first company and country profile are created</li>
              <li>You become the administrator, with seats to invite your team</li>
              <li>CRM opens on a guided setup checklist</li>
            </ul>
          </div>

          <p style={{ marginTop: 26 }}>
            <a className="btn btn--primary btn--lg" href={endpoints.signIn}>
              Go to sign in
            </a>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="wrap" style={{ maxWidth: 620 }}>
        <span className="eyebrow">Start free</span>
        <h1 style={{ marginBottom: 12 }}>Create your ClientMa account</h1>
        <p style={{ color: 'var(--muted)' }}>
          No card needed. You are setting up an organisation, not just a login — you will be its
          administrator.
        </p>

        <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
          {plan ? (
            <>
              Starting on <strong style={{ color: 'var(--heading)' }}>{plan.name}</strong>
              {plan.trialDays > 0 ? `, free for ${plan.trialDays} days` : ''}.{' '}
              <button type="button" className="linklike" onClick={() => navigate('/pricing')}>
                Change plan
              </button>
            </>
          ) : (
            <>
              Starting with <strong style={{ color: 'var(--heading)' }}>ClientMa CRM</strong>.{' '}
              <button type="button" className="linklike" onClick={() => navigate('/pricing')}>
                See plans
              </button>
            </>
          )}
        </p>

        {/* Said here rather than left to be discovered: nobody is committing to one product for
            good, and no decision about the others is being asked for now. */}
        <p style={{ color: 'var(--muted)', fontSize: '0.86rem', marginTop: -4 }}>
          The other applications are added later from Billing, each with its own subscription and
          seats — SRM and Inventory whenever you want them, HR once it arrives. Nothing here
          locks you to one application.
        </p>

        {error && (
          <div className="notice notice--error" role="alert">
            {error}
          </div>
        )}

        <form className="form" onSubmit={submit} style={{ marginTop: 28 }}>
          <div className="form__row">
            <label className="field">
              <span className="field__label">First name</span>
              <input
                className="input"
                value={firstName}
                autoComplete="given-name"
                required
                onChange={(e) => setFirstName(e.target.value)}
              />
            </label>

            <label className="field">
              <span className="field__label">Last name</span>
              <input
                className="input"
                value={lastName}
                autoComplete="family-name"
                required
                onChange={(e) => setLastName(e.target.value)}
              />
            </label>
          </div>

          <label className="field">
            <span className="field__label">Work email</span>
            <input
              className="input"
              type="email"
              value={email}
              autoComplete="email"
              required
              onChange={(e) => setEmail(e.target.value)}
            />
            <span className="field__hint">
              This becomes your sign-in, and we send the confirmation here.
            </span>
          </label>

          <label className="field">
            <span className="field__label">Password</span>
            <input
              className="input"
              type="password"
              value={password}
              autoComplete="new-password"
              required
              onChange={(e) => setPassword(e.target.value)}
            />
            <ul className="rules">
              {passwordRules.map((rule) => (
                <li
                  key={rule.label}
                  className={rule.test(password) ? 'rules__met' : undefined}
                  aria-live="polite"
                >
                  {rule.label}
                </li>
              ))}
            </ul>
          </label>

          <label className="field">
            <span className="field__label">Organisation name</span>
            <input
              className="input"
              value={companyName}
              autoComplete="organization"
              required
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </label>

          {/*
            An identifier, not an address.

            This said "Organisation address" with a .clientma.com suffix, which promised a
            per-tenant subdomain the platform does not have: nothing resolves a tenant from the
            request host, and everyone signs in at the same place and picks their organisation
            from a list. The suffix would have been a promise broken on the first sign-in.
          */}
          <label className="field">
            <span className="field__label">Organisation ID</span>
            <input
              className="input"
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(slugify(e.target.value));
              }}
            />
            <span className="field__hint">
              {slugState === 'checking' && 'Checking…'}
              {slugState === 'free' && `“${effectiveSlug}” is free.`}
              {slugState === 'taken' &&
                'That one is taken — we will pick the nearest free version of it.'}
              {slugState === 'idle' &&
                'How your organisation is identified when you sign in and when you contact support.'}
            </span>
          </label>

          <label className="field">
            <span className="field__label">Country</span>
            <select
              className="input"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            <span className="field__hint">
              Sets your currency, time zone, date format and tax treatment. Changeable later.
            </span>
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={withSamples}
              onChange={(e) => setWithSamples(e.target.checked)}
            />
            <span>
              Start with example records so there is something to look at. Clearly marked as
              samples, and safe to delete.
            </span>
          </label>

          {/*
            Three links rather than one sentence, and each opens over the form rather than
            navigating away: somebody halfway through a sign-up who follows a link loses what
            they have typed, and a consent that costs you your work to read is one nobody reads.
          */}
          <label className="check">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span>
              I accept the{' '}
              <button type="button" className="linkish" onClick={() => setLegal('terms')}>
                terms of service
              </button>
              , the{' '}
              <button type="button" className="linkish" onClick={() => setLegal('privacy')}>
                privacy policy
              </button>{' '}
              and the{' '}
              <button type="button" className="linkish" onClick={() => setLegal('pdp')}>
                personal data protection
              </button>{' '}
              terms, and I am authorised to create an account for this organisation.
            </span>
          </label>

          <button className="btn btn--primary btn--lg btn--block" type="submit" disabled={busy}>
            {busy ? 'Creating your organisation…' : 'Create account'}
          </button>

          <p style={{ textAlign: 'center', margin: '18px 0 0', fontSize: '0.92rem' }}>
            Already have an account? <a href={endpoints.signIn}>Sign in</a>
          </p>
        </form>

        {legal && <LegalDialog openAt={legal} onClose={() => setLegal(null)} />}
      </div>
    </section>
  );
}
